import React, { useEffect, useCallback, useMemo } from 'react';
import { Box, FormControl, Select, MenuItem, SelectChangeEvent, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSettingsYears, useUserPreferences } from '@/hooks';

interface YearSelectorProps {
  selectedYear: string;
  /** Called when the user selects a new year (after any unsaved-changes guard). */
  onYearChange: (year: string) => void;
  /** If true, the parent will be asked to confirm before the year actually changes. */
  hasUnsavedChanges?: boolean;
  /** Called when a year change is blocked by unsaved changes.
   *  The parent should show a confirmation dialog and, if confirmed, call `onYearChange` with the pending year. */
  onUnsavedYearChange?: (pendingYear: string) => void;
  disabled?: boolean;
  /** Called whenever the set of years with saved custom preferences is loaded or updated. */
  onCustomizedYears?: (years: Set<string>) => void;
}

/**
 * Year selector dropdown.
 *
 * Uses two React-Query hooks:
 *  - `useSettingsYears()` — full list of years from the Settings superset.
 *  - `useUserPreferences()` — all saved preferences (to derive which years are customized).
 *
 * When `hasUnsavedChanges` is true and the user picks a different year,
 * the component calls `onUnsavedYearChange` instead of `onYearChange`,
 * letting the parent decide whether to proceed.
 */
const YearSelector: React.FC<YearSelectorProps> = ({
  selectedYear,
  onYearChange,
  hasUnsavedChanges = false,
  onUnsavedYearChange,
  disabled = false,
  onCustomizedYears,
}) => {
  const { t } = useTranslation();

  // Available years from Settings superset (all years, not preference-filtered)
  const { years: availableYears, isLoading: isLoadingYears, error: yearsError } = useSettingsYears();

  // All saved user preferences (to derive customized years)
  const { preferences, isLoading: isLoadingPrefs } = useUserPreferences();

  const isLoading = isLoadingYears || isLoadingPrefs;
  const error = yearsError ? String(yearsError) : null;

  // Derive the set of years that have saved custom preferences
  // Only count years where at least one module is enabled — all-disabled is "no customisation"
  const customizedYears = useMemo(() => {
    const years = new Set<string>();
    preferences.forEach((pref) => {
      if (pref.modules.some((m) => m.enabled)) {
        years.add(String(pref.accountingYear));
      }
    });
    return years;
  }, [preferences]);

  // Notify parent whenever customized years change
  useEffect(() => {
    onCustomizedYears?.(customizedYears);
  }, [customizedYears]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-select: first customized year (desc) if any, otherwise first available year
  useEffect(() => {
    if (isLoading || selectedYear || availableYears.length === 0) return;

    const sortedCustomized = Array.from(customizedYears)
      .filter((y) => availableYears.includes(y))
      .sort((a, b) => Number(b) - Number(a));

    onYearChange(sortedCustomized.length > 0 ? sortedCustomized[0] : availableYears[0]);
  }, [isLoading, availableYears, customizedYears]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = useCallback(
    (event: SelectChangeEvent) => {
      const newYear = event.target.value as string;

      if (hasUnsavedChanges && onUnsavedYearChange) {
        onUnsavedYearChange(newYear);
      } else {
        onYearChange(newYear);
      }
    },
    [hasUnsavedChanges, onUnsavedYearChange, onYearChange],
  );

  // Sorted list of years with custom preferences (for the hint text)
  const customizedYearsList = useMemo(
    () => Array.from(customizedYears).sort().reverse(),
    [customizedYears],
  );

  return (
    <Box sx={{ minWidth: 200 }}>
      <Box sx={{ mb: 1 }}>
        <Box component="span" sx={{ fontWeight: 'bold' }}>
          {t('userPreferences.yearSelector.label', 'Filtering Accounting Year')}
        </Box>
      </Box>
      <FormControl variant="outlined" sx={{ width: 200, mt: 1 }}>
        <Select
          id="user-preferences-year-select"
          value={selectedYear}
          onChange={handleChange}
          data-testid="user-preferences-year-select"
          size="small"
          disabled={disabled || isLoading || error !== null}
        >
          {isLoading ? (
            <MenuItem disabled>
              {t('userPreferences.yearSelector.loading', 'Loading years...')}
            </MenuItem>
          ) : error ? (
            <MenuItem disabled>
              {t('userPreferences.yearSelector.errorLoading', 'Failed to load available years')}
            </MenuItem>
          ) : (
            availableYears.map((year) => (
              <MenuItem
                key={year}
                value={year}
                sx={customizedYears.has(year) ? { fontWeight: 'bold' } : undefined}
              >
                {year}
              </MenuItem>
            ))
          )}
        </Select>
      </FormControl>
    </Box>
  );
};

export default YearSelector;

